import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { firstValueFrom } from 'rxjs';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { CustomChartReport } from 'src/app/interfaces/custom-chart-report/custom-chart-report.interface';
import { CustomTableReport } from 'src/app/interfaces/custom-table-report/custom-table-report.interface';
import { InvoicingReportFilterModel } from 'src/app/models/invoicing/invoicing-report-filter.model';
import { DateConverterService } from 'src/app/services/global-services/date-converter.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { InvoicingReportService } from 'src/app/services/invoicing/invoicing-report.service';
import { BranchService } from 'src/app/services/management/branch.service';
import { ConceptInvoicingService } from 'src/app/services/invoicing/concept-invoicing.service';
import { EnterpriseService } from 'src/app/services/management/enterprise.service';
import { RentalPeriodService } from 'src/app/services/rental/rental-period.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { CustomValidators } from 'src/app/validators/custom-validators';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-invoicing-family',
  templateUrl: './invoicing-family.component.html',
  styleUrls: ['./invoicing-family.component.scss'],
  standalone: false
})
export class InvoicingFamilyComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('filtersContainer') filtersContainer!: ElementRef;

  changeFilters: boolean = false;
  customChartReport: CustomChartReport;
  dataReport: CustomTableReport;
  filterObserver?: ResizeObserver;
  filtersHeight: number = 0;
  filteredEnterprise: any[] = [];
  formInvoicingReportFilter: FormGroup = new FormGroup({});
  listBranch: any[] = [];
  listConceptInvoicing: any[] = [];
  listEnterprise: any[] = [];
  nameWindow: string = 'Informe de Ingresos por Familia ';
  projectName = ProjectName.projectName;

  constructor(private branchService: BranchService,
    private conceptInvoicingService: ConceptInvoicingService,
    private dateConverterService: DateConverterService,
    private enterpriseService: EnterpriseService,
    private formBuilder: FormBuilder,
    private headerService: HeaderService,
    private invoicingReportService: InvoicingReportService,
    private rentalPeriodService: RentalPeriodService,
    private title: Title,
    private toastHandlerService: ToastHandlerService) {
    this.validateForm();
  }

  async ngOnInit() {
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    await this.getAllLists();
    this.getFirstBranchByUserAuthenticatedAsync();
  }

  ngAfterViewInit() {
    this.filterObserver = new ResizeObserver(() => {
      this.filtersHeight = this.filtersContainer?.nativeElement?.offsetHeight || 0;
    });
    this.filterObserver.observe(this.filtersContainer.nativeElement);
  }

  ngOnDestroy() {
    this.filterObserver?.disconnect();
  }

  changeBranch(branchId: number | null) {
    this.filterEnterpriseByBranchId(branchId);
    if (branchId != 0 && branchId != null) {
      this.getFirstEnterpriseByBranch(branchId);
    } else {
      this.formInvoicingReportFilter.controls["enterpriseId"].patchValue(0);
      this.formInvoicingReportFilter.controls["branchId"].patchValue(0);
    }
    this.changeFilter();
  }

  changeConceptInvoicing(conceptInvoicingId: number | null) {
    if (conceptInvoicingId === 0 || conceptInvoicingId === null || conceptInvoicingId === undefined) {
      this.formInvoicingReportFilter.controls["conceptInvoicingId"].patchValue(0);
    }
    this.changeFilter();
  }

  changeEnterprise(enterpriseId: number | null) {
    if (enterpriseId != 0 && enterpriseId != null) {
      const branchId = this.listEnterprise.find(x => x.value === enterpriseId)!.branchId;
      this.formInvoicingReportFilter.controls["branchId"].patchValue(branchId);
    } else {
      this.formInvoicingReportFilter.controls["enterpriseId"].patchValue(0);
    }
    this.changeFilter();
  }

  changeFilter() {
    this.changeFilters = false;
  }

  clear() {
    this.changeFilter();
    this.validateForm();
    this.getFirstBranchByUserAuthenticatedAsync();
  }

  async exportExcel() {
    if (this.changeFilters) {
      const modelRentalReportFilter = this.formInvoicingReportFilter.value as InvoicingReportFilterModel;
      try {
        const response = await firstValueFrom(this.invoicingReportService.exportInvoicingReportInvoicingFamily(modelRentalReportFilter));
        const blob = response;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Informe de Ingresos por Familia.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error al exportar el informe.', error);
      }
    } else {
      this.toastHandlerService.showToast(
        'info',
        'No se encontraron datos para exportar.'
      );
    }
  }

  filterEnterpriseByBranchId(branchId: number | null) {
    if (branchId != null) {
      this.filteredEnterprise = this.listEnterprise.filter(x => x.branchId === branchId);
      this.formInvoicingReportFilter.controls["enterpriseId"].patchValue(0);
    } else {
      this.filteredEnterprise = this.listEnterprise;
    }
  }

  async getAllLists() {
    try {
      const [branches, enterprises, conceptsInvoicing] = await Promise.all([
        firstValueFrom(this.branchService.getAllBranchesForSelectAsync()),
        firstValueFrom(this.enterpriseService.getAllEnterprisesForSelectAsync()),
        firstValueFrom(this.conceptInvoicingService.getAllConceptsInvoicingForSelectAsync()),
      ]);
      this.listBranch = branches;
      this.listEnterprise = enterprises;
      this.filteredEnterprise = this.listEnterprise;
      this.listConceptInvoicing = conceptsInvoicing;
      return true;
    } catch (error) {
      console.error('Error al obtener las listas:', error);
      return false;
    }
  }

  async getFirstBranchByUserAuthenticatedAsync() {
    try {
      const branch = await firstValueFrom(this.branchService.getFirstBranchByUserAuthenticatedAsync());
      this.formInvoicingReportFilter.controls["branchId"].patchValue(branch.branchId);
      this.getLastRentalPeriod(branch.branchId);
      this.filterEnterpriseByBranchId(branch.branchId);
      this.getFirstEnterpriseByBranch(branch.branchId);
    } catch (error) {
      console.error("Error al consultar la sucursal:", error);
    }
  }

  async getFirstEnterpriseByBranch(branchId: number) {
    try {
      const response = await firstValueFrom(this.enterpriseService.getFirstEnterpriseByBranch(branchId));
      if (!response) {
        this.toastHandlerService.showToast('error', 'La Sucursal no tiene empresas creadas.');
      } else {
        this.formInvoicingReportFilter.controls["enterpriseId"].patchValue(response.enterpriseId);
      }
    } catch (error) {
      console.error('Error al obtener la primera empresa por sucursal.', error);
    }
  }


  async getLastRentalPeriod(branchId: number) {
    try {
      const rentalPeriod = await firstValueFrom(this.rentalPeriodService.getLastRentalPeriod(branchId));
      this.formInvoicingReportFilter.controls["initialDate"].patchValue(this.dateConverterService.convertDatePreserveDay(rentalPeriod.initialDate));
      this.formInvoicingReportFilter.controls["finalDate"].patchValue(this.dateConverterService.convertDatePreserveDay(rentalPeriod.finalDate));
    } catch (error) {
      console.error("Error al consultar el periodo de alquiler:", error);
    }
  }

  printReport() {
    if (this.changeFilters) {
      window.print();
    } else {
      this.toastHandlerService.showToast(
        'info',
        'No se encontraron datos para imprimir.'
      );
    }
  }

  async send() {
    Object.keys(this.formInvoicingReportFilter.controls).forEach(key => {
      const control = this.formInvoicingReportFilter.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, 'value:', control.value);
      }
    });
    if (this.formInvoicingReportFilter.valid) {
      const modelRentalReportFilter = this.formInvoicingReportFilter.value as InvoicingReportFilterModel;

      if (modelRentalReportFilter.finalDate < modelRentalReportFilter.initialDate) {
        Swal.fire({
          icon: 'warning',
          title: 'Atención.',
          text: 'La fecha final no puede ser menor a la fecha inicial.'
        });
        return;
      }

      try {
        this.dataReport = await firstValueFrom(this.invoicingReportService.getInvoicingFamily(modelRentalReportFilter));
        if (this.dataReport.data && this.dataReport.data.length != 0 && this.dataReport.data.length != null) {
          this.changeFilters = true;
          //Capturo los datos para la grafica
          const result: any = [];
          this.dataReport.data.forEach(row => {
            result.push(
              {
                name: row.familyCode,
                value: row.totalValue
              }
            );
          });
          this.customChartReport = {
            result: result,
            legendTitle: '',
            showXAxisLabel: true,
            xAxisLabel: 'Familia',
            showYAxisLabel: true,
            yAxisLabel: 'Vr. Facturado'
          };
        } else {
          this.changeFilters = false;
          this.toastHandlerService.showToast('info', 'No se encontraron datos.');
        }
      } catch (error) {
        console.error("Error al consultar el informe:", error);
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      initialDate: {
        required: 'El campo Desde es obligatorio.'
      },
      finalDate: {
        required: 'El campo Hasta es obligatorio.'
      },
      conceptInvoicingId: {
        notNumber: 'Debe seleccionar un Concepto válido.'
      }
    };

    Object.keys(controlMessages).forEach((control) => {
      const formControl = this.formInvoicingReportFilter.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  validateForm() {
    this.formInvoicingReportFilter = this.formBuilder.group({
      initialDate: ['', [Validators.required]],
      finalDate: ['', [Validators.required]],
      branchId: [0, [Validators.min(1)]],
      enterpriseId: [0],
      conceptInvoicingId: [0, [CustomValidators.numberOnlyValidator()]]
    });
  }

}
